import { create } from 'zustand';
import { jobService, backendProviderService, searchService } from '@/services';
import { useAuthStore } from '@/store/authStore';
import type { Job, JobCreateRequest, SearchFilters, ServiceProvider } from '@/types';

interface JobState {
  jobs: Job[];
  providers: ServiceProvider[];
  selectedJob: Job | null;
  searchResults: ServiceProvider[];
  isLoading: boolean;
  searchFilters: SearchFilters;

  // Actions
  fetchJobs: () => Promise<void>;
  fetchProviders: () => Promise<void>;
  searchProviders: (query: string, location: string, filters?: SearchFilters) => Promise<void>;
  createJob: (jobData: JobCreateRequest) => Promise<Job>;
  setSelectedJob: (job: Job | null) => void;
  setSearchFilters: (filters: SearchFilters) => void;
  clearSearch: () => void;
}

// No mock data - using real Supabase data

export const useJobStore = create<JobState>((set) => ({
  jobs: [],
  providers: [],
  selectedJob: null,
  searchResults: [],
  isLoading: false,
  searchFilters: {},

  fetchJobs: async () => {
    set({ isLoading: true });
    try {
      console.log('🔄 JobStore: Fetching jobs from backend...');
      const result = await jobService.getMyJobs();
      console.log('✅ JobStore: Jobs fetched from backend:', result.jobs?.length || 0);
      // Log all jobs for debugging
      if (result.jobs && result.jobs.length > 0) {
        console.log('📋 All jobs from backend:');
        result.jobs.forEach((job, index) => {
          console.log(`  Job ${index + 1}:`, {
            id: job.id,
            title: job.title,
            status: job.status,
            assignedProviderId: job.assignedProviderId,
            providerId: (job as any).providerId,
            quoteCount: job.quoteCount,
            customerId: job.customerId
          });
        });
      }
      set({ jobs: result.jobs || [], isLoading: false });
    } catch (error) {
      console.error('❌ JobStore: Error fetching jobs:', error);
      set({ jobs: [], isLoading: false });
    }
  },

  fetchProviders: async () => {
    set({ isLoading: true });
    try {
      const providers = await backendProviderService.getProviders();
      set({ providers, isLoading: false });
    } catch (error) {
      console.error('Error fetching providers:', error);
      set({ providers: [], isLoading: false });
    }
  },

  searchProviders: async (query, location, filters = {}) => {
    set({ isLoading: true });
    try {
      const searchFilters = {
        query,
        location,
        verified: filters.verified,
        availability: filters.availability === 'now' ? 'now' as const : undefined,
        minRating: filters.minRating,
        sortBy: filters.sortBy === 'rating' ? 'rating' as const :
          filters.sortBy === 'price_low' ? 'price' as const : 'relevance' as const
      };

      const response = await searchService.searchProviders(searchFilters);
      const results = response.providers || [];

      set({ searchResults: results, isLoading: false, searchFilters: filters });
    } catch (error) {
      console.error('Error searching providers:', error);
      set({ searchResults: [], isLoading: false });
    }
  },

  createJob: async (jobData) => {
    set({ isLoading: true });
    try {
      console.log('🔧 Job creation started with raw data:', jobData);

      // First, get the category ID from the category name
      let categoryId = jobData.category;

      // If category is a name (not UUID), find the corresponding ID
      if (jobData.category && !jobData.category.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        console.log('🔍 Category is not UUID, looking up category ID for:', jobData.category);
        try {
          const categories = await jobService.getJobCategories();
          console.log('📋 Available categories:', categories);

          const matchingCategory = categories.find((cat: any) =>
            cat.name.toLowerCase() === jobData.category.toLowerCase() ||
            cat.slug === jobData.category.toLowerCase().replace(/\s+/g, '-')
          );

          if (matchingCategory) {
            categoryId = matchingCategory.id;
            console.log('✅ Found matching category:', { name: matchingCategory.name, id: categoryId });
          } else {
            // If no matching category found, use the first available category
            categoryId = categories.length > 0 ? categories[0].id : 'home-cleaning';
            console.warn('⚠️ Category not found, using first available:', jobData.category, '→', categoryId);
          }
        } catch (error) {
          console.error('❌ Failed to fetch categories for job creation:', error);
          // Continue with original category value
        }
      } else {
        console.log('✅ Category is already a UUID:', categoryId);
      }

      const createJobData = {
        title: jobData.title,
        description: jobData.description,
        category_id: categoryId, // Backend expects category_id with UUID
        location: `${jobData.address.houseNumber} ${jobData.address.streetName}, ${jobData.address.area}, ${jobData.address.eircode}`,
        priority: jobData.urgency.toLowerCase(), // Backend expects priority, not urgency
        budget_min: jobData.budgetRange?.min || 0, // Backend expects budget_min
        budget_max: jobData.budgetRange?.max || jobData.budgetRange?.min || 0, // Backend expects budget_max
        scheduled_date: jobData.preferredDate ? new Date(jobData.preferredDate).toISOString() : undefined, // Backend expects scheduled_date
        images: jobData.images?.map(file => file.name) || [],
      };

      console.log('📤 Sending job creation request with data:', createJobData);

      // Get auth info from auth store
      const { user, token } = useAuthStore.getState();
      console.log('🔐 User authentication:', {
        isAuthenticated: !!user,
        userId: user?.id || 'unknown',
        token: token ? 'present' : 'missing'
      });

      const newJob = await jobService.createJob(createJobData);

      set(state => ({
        jobs: [newJob, ...state.jobs],
        isLoading: false,
      }));

      return newJob;
    } catch (error) {
      console.error('❌ Error creating job:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  setSelectedJob: (job) => {
    set({ selectedJob: job });
  },

  setSearchFilters: (filters) => {
    set({ searchFilters: filters });
  },

  clearSearch: () => {
    set({ searchResults: [], searchFilters: {} });
  },
}));